import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';

const Services = () => {
  const { t } = useTranslation();
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);

  // Fallback services data
  const fallbackServices = [
    {
      id: '1',
      title: 'Modular Operation Theatre',
      description: 'Complete design, manufacturing, and installation of Modular Operation Theatres with all necessary components for sterile surgical environment.',
      image: '',
      feature1: 'Complete modular design',
      feature2: 'SS304 construction',
      feature3: 'Customizable configurations',
      feature4: 'Professional installation',
    },
    {
      id: '2',
      title: 'PSA Oxygen Generation Plant',
      description: 'Installation of on-site Oxygen Generation Plants using PSA technology for uninterrupted oxygen supply.',
      image: '',
      feature1: 'On-site oxygen production',
      feature2: 'Cost-efficient',
      feature3: 'Uninterrupted supply',
      feature4: 'PSA technology',
    },
    {
      id: '3',
      title: 'Air Quality Management',
      description: 'Complete air quality solutions including Laminar Air Flow systems, Air Handling Units, and pollution control.',
      image: '',
      feature1: 'HEPA filtration',
      feature2: 'Positive pressure maintenance',
      feature3: 'UV sterilization',
      feature4: 'Air quality monitoring',
    },
    {
      id: '4',
      title: 'Isolation Ward Setup',
      description: 'Design and installation of isolation wards with advanced air purification and filtration systems.',
      image: '',
      feature1: 'Negative pressure rooms',
      feature2: 'Air purification',
      feature3: 'COVID-19 response',
      feature4: 'Healthcare worker protection',
    },
    {
      id: '5',
      title: 'Disinfection Systems',
      description: 'Installation and maintenance of Ozone Generators and other disinfection systems for healthcare facilities.',
      image: '',
      feature1: 'Chemical-free disinfection',
      feature2: 'Bacteria/virus elimination',
      feature3: 'Safe operation',
      feature4: 'Quick disinfection',
    },
    {
      id: '6',
      title: 'Maintenance & Support',
      description: '24x7 maintenance and support services for all installed equipment. AMC contracts available.',
      image: '',
      feature1: '24x7 support',
      feature2: 'Preventive maintenance',
      feature3: 'Spare parts',
      feature4: 'Emergency service',
    }
  ];

  // Default icons for services (used when no image)
  const defaultIcons = ['🏥', '⚗️', '💨', '🛡️', '🦠', '🔧'];

  useEffect(() => {
    const fetchServices = async () => {
      try {
        const response = await axios.get('/api/services');
        if (response.data && response.data.length > 0) {
          setServices(response.data);
        } else {
          setServices(fallbackServices);
        }
      } catch (error) {
        console.error('Error fetching services:', error);
        setServices(fallbackServices);
      } finally {
        setLoading(false);
      }
    };

    fetchServices();
  }, []);

  // Get features as array
  const getFeatures = (service) => {
    const features = [];
    if (service.feature1) features.push(service.feature1);
    if (service.feature2) features.push(service.feature2);
    if (service.feature3) features.push(service.feature3);
    if (service.feature4) features.push(service.feature4);
    return features;
  };

  return (
    <div className="services-page" style={{marginTop: '70px'}}>
      {/* Hero Section */}
      <section className="hero" style={{minHeight: '50vh'}}>
        <div className="hero-content">
          <p className="hero-subtitle">{t('services.badge')}</p>
          <h1 className="hero-title">{t('services.pageTitle')}</h1>
          <p className="hero-description">
            {t('services.pageDescription')}
          </p>
        </div>
      </section>

      {/* Services Grid */}
      <section className="section">
        <div className="container">
          <div className="section-header">
            <h2 className="section-title">{t('services.whatWeOffer.title')}</h2>
            <p className="section-description">
              {t('services.whatWeOffer.description')}
            </p>
          </div>

          {loading ? (
            <div style={{ textAlign: 'center', padding: '60px 0' }}>
              <div className="loading-spinner"></div>
              <p style={{ marginTop: '20px', color: 'var(--text-secondary)' }}>{t('services.loading')}</p>
            </div>
          ) : (
            <div className="products-grid">
              {services.map((service, index) => (
                <div key={service.id} className="product-card service-card">
                  <div className="product-card-image service-card-image">
                    {service.image ? (
                      <img 
                        src={service.image} 
                        alt={service.title}
                        style={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'cover',
                          borderRadius: 'var(--radius-lg) var(--radius-lg) 0 0'
                        }}
                      />
                    ) : (
                      <span style={{ fontSize: '4rem' }}>
                        {defaultIcons[index] || '🏥'}
                      </span>
                    )}
                  </div>
                  <div className="product-card-content">
                    <h3 className="product-card-title">{service.title}</h3>
                    <p className="product-card-description">{service.description}</p>
                    <ul className="service-features-list">
                      {getFeatures(service).map((feature, idx) => (
                        <li key={idx} className="service-feature-item">
                          <span className="feature-check">✓</span> {feature}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Process Section */}
      <section className="section" style={{backgroundColor: 'var(--bg-secondary)'}}>
        <div className="container">
          <div className="section-header">
            <h2 className="section-title">{t('services.process.title')}</h2>
            <p className="section-description">
              {t('services.process.description')}
            </p>
          </div>

          <div className="discover-grid">
            <div className="discover-card">
              <div className="discover-card-content" style={{textAlign: 'center'}}>
                <div style={{fontSize: '3rem', marginBottom: '1rem'}}>1️⃣</div>
                <h3 className="discover-card-title">{t('services.process.consultation.title')}</h3>
                <p className="discover-card-description">
                  {t('services.process.consultation.description')}
                </p>
              </div>
            </div>

            <div className="discover-card">
              <div className="discover-card-content" style={{textAlign: 'center'}}>
                <div style={{fontSize: '3rem', marginBottom: '1rem'}}>2️⃣</div>
                <h3 className="discover-card-title">{t('services.process.design.title')}</h3>
                <p className="discover-card-description">
                  {t('services.process.design.description')}
                </p>
              </div>
            </div>

            <div className="discover-card">
              <div className="discover-card-content" style={{textAlign: 'center'}}>
                <div style={{fontSize: '3rem', marginBottom: '1rem'}}>3️⃣</div>
                <h3 className="discover-card-title">{t('services.process.manufacturing.title')}</h3>
                <p className="discover-card-description">
                  {t('services.process.manufacturing.description')}
                </p>
              </div>
            </div>

            <div className="discover-card">
              <div className="discover-card-content" style={{textAlign: 'center'}}>
                <div style={{fontSize: '3rem', marginBottom: '1rem'}}>4️⃣</div>
                <h3 className="discover-card-title">{t('services.process.installation.title')}</h3>
                <p className="discover-card-description">
                  {t('services.process.installation.description')}
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="cta-section">
        <div className="container">
          <h2 className="cta-title">{t('services.cta.title')}</h2>
          <p className="cta-description">
            {t('services.cta.description')}
          </p>
          <div className="hero-buttons">
            <Link to="/contact" className="btn btn-white">{t('services.cta.getFreeQuote')}</Link>
            <a href="tel:+919322510636" className="btn btn-secondary" style={{borderColor: 'white', color: 'white'}}>
              {t('services.cta.call')}: +91 93225 10636
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Services;
